/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import { View } from 'backbone.marionette';
import { Radio } from 'backbone';

import GridView from '@/views/GridView';
import PageableCollection from '@/backbone/PageableCollection';
import ButtonCell from '@/backgrid/ButtonCell';
import StringCell from '@/backgrid/StringCell';
import { DateCell } from '@/backgrid/MomentCell';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import openInNewTab from '@/util/openInNewTab';

import SaveAWPackageOrderPackagesSelection from '../../services/SaveAWPackageOrderPackagesSelection';

import './AWPackageSelectOPsView.css';

import tmplAWPackageSelectOPsView from './AWPackageSelectOPsView.hbs';

const AWPackageSelectOPsView = View.extend({
  template: tmplAWPackageSelectOPsView,

  className: 'aw-package-select-orders-view',

  ui: {
    approvedOrderPackagesGrid: '.approved-order-packages-grid',
    packagedOrderPackagesGrid: '.packaged-order-packages-grid'
  },

  regions: {
    approvedOrderPackagesGrid: '@ui.approvedOrderPackagesGrid',
    packagedOrderPackagesGrid: '@ui.packagedOrderPackagesGrid'
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
  },

  onRender() {
    const approvedOrderPackages = this.model.get('approvedOrderPackages');
    const packagedOrderPackages = this.model.get('packagedOrderPackages');
    const editable = this.model.get('awPackageDetails').editable;

    const commonColumns = [
      {
        name: 'customerOrderIds',
        label: 'Order #(s)',
        cell: StringCell,
        headerCell: 'custom',
        width: 9,
        editable: false
      },
      {
        name: 'id',
        label: 'Order Package ID',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false
      },
      {
        name: 'crsName',
        label: 'Assigned CRS',
        cell: StringCell,
        headerCell: 'custom',
        width: 11,
        editable: false
      },
      {
        name: 'formName',
        label: 'Order Type',
        cell: StringCell,
        headerCell: 'custom',
        width: 13,
        editable: false
      },
      {
        name: 'deviceCount',
        label: '# Devices',
        cell: StringCell,
        headerCell: 'custom',
        width: 9,
        editable: false
      },
      {
        name: 'internalOrderStatus',
        label: 'Order Status',
        cell: StringCell,
        headerCell: 'custom',
        width: 8,
        editable: false
      },
      {
        name: 'trackerNumbers',
        label: 'Tracker #(s)',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false
      },
      {
        name: 'createdOn',
        label: 'Date Created',
        cell: DateCell,
        headerCell: 'custom',
        width: 9,
        editable: false
      },
      {
        name: 'popStartDate',
        label: 'POP Start Date',
        cell: DateCell,
        headerCell: 'custom',
        width: 9,
        editable: false
      },
      {
        name: 'open',
        label: '',
        width: 10,
        cell: ButtonCell.extend({
          name: 'Open',
          buttonClass: 'btn-sm btn-outline-primary',
          onClick: this.viewDetails.bind(this),
          newTab: true
        }),
        editable: false,
        sortable: false
      }
    ];

    if (editable) {
      this.approvedOrderPackagesGrid = new GridView({
        columns: [
          {
            name: 'add',
            label: '',
            width: 5,
            cell: ButtonCell.extend({
              name: '',
              buttonClass: 'btn-sm btn-success text-white add-remove-symbol',
              disabled: !editable,
              labelClass: 'fa fa-plus fa-lg fa-fw text-success',
              ariaLabel: 'Add Order Package to AW Package',
              onClick: this.addToPackage.bind(this)
            }),
            editable: false,
            sortable: false,
            renderable: editable
          }
        ].concat(commonColumns),
        collection: new PageableCollection(approvedOrderPackages, { pageSize: 7 }),
        emptyText: 'No Approved Order Packages Found'
      });

      this.showChildView('approvedOrderPackagesGrid', this.approvedOrderPackagesGrid);
    }

    this.packagedOrderPackagesGrid = new GridView({
      columns: [
        {
          name: 'remove',
          label: '',
          width: 5,
          cell: ButtonCell.extend({
            name: '',
            buttonClass: 'btn-sm btn-danger text-white add-remove-symbol',
            disabled: !editable,
            labelClass: 'fa fa-minus fa-lg fa-fw text-danger',
            ariaLabel: 'Remove Order Package from AW Package',
            onClick: this.removeFromPackage.bind(this)
          }),
          editable: false,
          sortable: false,
          renderable: editable
        }
      ].concat(commonColumns),
      collection: new PageableCollection(packagedOrderPackages, { pageSize: 7 }),
      emptyText: 'No Consolidated Order Packages'
    });

    this.showChildView('packagedOrderPackagesGrid', this.packagedOrderPackagesGrid);
  },

  addToPackage(e, model) {
    const length = this.packagedOrderPackagesGrid.collection.fullCollection.length;
    const modelOrderFormTypeId = model.get('orderFormTypeId');

    if (
      length
        ? this.packagedOrderPackagesGrid.collection.fullCollection.first().get('orderFormTypeId') ===
          modelOrderFormTypeId
        : true
    ) {
      this.approvedOrderPackagesGrid.collection.fullCollection.remove(model);
      this.packagedOrderPackagesGrid.collection.fullCollection.unshift(model);
    } else {
      showErrorMessage({
        errorCode: 'orderTypeError',
        errorTitle: 'Order Type Error',
        errorMessage: 'Only order packages of the same type can be added.'
      });
    }
  },

  removeFromPackage(e, model) {
    this.packagedOrderPackagesGrid.collection.fullCollection.remove(model);
    this.approvedOrderPackagesGrid.collection.fullCollection.unshift(model);
  },

  viewDetails(e, model) {
    e.preventDefault();
    openInNewTab(`#order-package/${model.id}`);
  },

  async onSave() {
    const editable = this.model.get('awPackageDetails')?.editable;
    if (!editable) {
      return;
    }

    this.toggleForm(false);
    const awPackageId = this.model.get('awPackageDetails').awPackageId;

    const firstPackagedOrder = this.packagedOrderPackagesGrid.collection.fullCollection.first();
    const formName = firstPackagedOrder ? firstPackagedOrder.get('formName') : '--';
    const orderFormTypeId = firstPackagedOrder ? firstPackagedOrder.get('orderFormTypeId') : null;

    const orderPackages = {
      OrderPackage: _.map(this.packagedOrderPackagesGrid.collection.fullCollection.toJSON(), (x) => {
        return { ID: x.id };
      })
    };

    try {
      await SaveAWPackageOrderPackagesSelection({ awPackageId, orderFormTypeId, orderPackages });

      showSuccessMessage({
        code: 'saveSuccess',
        title: 'Save Success',
        message: 'AW Package Order Packages Saved.',
        hideAfter: 1
      });

      // Flip statuses in the grids
      _.each(this.packagedOrderPackagesGrid.collection.fullCollection.models, (model) => {
        model.set('internalOrderStatus', 'Packaged');
        model.set('statusID', '6');
      });

      _.each(this.approvedOrderPackagesGrid.collection.fullCollection.models, (model) => {
        model.set('internalOrderStatus', 'Approved');
        model.set('statusID', '5');
      });

      Radio.channel('aw-package').trigger('update:header', 'formName', formName);
    } catch (e) {
      // Empty block to prevent errors from reaching the console
    } finally {
      this.toggleForm(true);
    }
  }
});

export default AWPackageSelectOPsView;
